import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';

import { PageHeader } from '../../../components';
import { ResultTabs } from '../components/result';
import { loadDiagnosis, previousDiagnosis } from '../record';

import type { MetricTabId } from '../resultCopy';

/**
 * 진단 결과 상세 — `/result/:id/:tab` (시안 §8.2).
 *
 * **주소가 정본이다.** 탭을 누르면 주소가 바뀌고, 그 주소를 그대로 열면 같은 탭이 선다 —
 * 결과를 공유하는 화면이라(헤더 우측이 공유 아이콘이다) 링크가 어느 지표를 말하는지까지
 * 담아야 한다. 탭 상태를 화면 안에만 두면 공유한 링크가 늘 첫 탭으로 열린다.
 *
 * 회차는 **저장된 것**을 id로 읽는다(`loadDiagnosis`). 진단 직후의 결과 화면
 * (`ResultView`)과 다른 자리다: 그쪽은 방금 잰 회차를 컨텍스트에서 받아 그 세션 안에서만
 * 산다. 여기는 이력에서 다시 여는 길이라 새로고침에도 살아남는다.
 *
 * 히스토리를 **쌓지 않는다**(`replace`): 탭 일곱을 훑고 나서 뒤로 가기를 누르면 왔던
 * 화면이 아니라 방금 지나온 탭들을 거꾸로 되짚게 된다.
 */

/** 못 읽는 회차(지워졌거나 옛 스키마)는 이력으로 돌려보낸다 — 빈 결과 화면을 세우지 않는다. */
const FALLBACK = '/history';

export const ResultDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, tab } = useParams<{ id: string; tab?: string }>();

  // localStorage를 읽는다 — 렌더마다 다시 읽지 않도록 id가 바뀔 때만 뽑는다.
  const record = useMemo(() => (id ? loadDiagnosis(id) : null), [id]);
  const previous = useMemo(
    () => (record ? previousDiagnosis(record) : null),
    [record],
  );

  if (!record) {
    return <Navigate to={FALLBACK} replace />;
  }

  return (
    <div className="flex min-h-dvh w-full flex-col bg-background pb-10">
      {/* 공유는 아직 붙일 곳이 없다 — 회차는 이 기기의 localStorage 에만 있고 공유할
          주소가 밖에서 열리지 않는다. 아이콘만 남겨 두면 `aria-hidden` 이라 낭독되지도
          않으면서 화면에서는 컨트롤과 구분되지 않는다 — 눌러도 아무 일이 없는 것을
          그리느니 자리를 비운다. 공유가 생기면 그때 버튼으로 넣는다. */}
      <PageHeader title={t('web.result.detail.title')} />

      <ResultTabs
        record={record}
        previous={previous}
        tab={tab as MetricTabId | undefined}
        onTabChange={(next) =>
          navigate(`/result/${record.id}/${next}`, { replace: true })
        }
      />
    </div>
  );
};
